import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Toggle, TooltipHost, Icon } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { ApiResponse } from 'apisauce';
import { HealAPI } from '../../healApi';
import { Visit } from '../../redux/visits/types';
import styles from './VisitWaitlistToggle.module.scss';

interface Props {
  visit: Visit;
  onSuccess?: (title: string, content: string, visitCode?: string) => void;
  onError?: (content: string) => void;
}

/**
 * The text string used for both the tooltip and the screen reader-accessible description.
 */
const TOGGLE_DESCRIPTION = `Indicate that the patient is willing to be seen sooner, so this visit can be moved up in the event of cancellations.`;

/**
 * The title for the Waitlisted field with an info tooltip describing the its purpose.
 */
export const VisitWaitlistTitle = () => {
  return (
    <>
      Waitlisted
      <TooltipHost hostClassName={styles.waitlistTooltipWrapper} content={TOGGLE_DESCRIPTION}>
        <Icon iconName="info" className={styles.waitlistInfoIcon} aria-hidden="true"/>
      </TooltipHost>
    </>
  );
}

/**
 * A toggle button that controls a visit's isWaitlisted status.
 */
export const VisitWaitlistToggle = ({ visit, onError, onSuccess }: Props) => {
  const toggleDescriptionId = useId();

  const [isWaitlisted, setIsWaitlisted] = useState(visit.isWaitlisted);
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Initialize an AbortController to help prevent unmounted rerenders in the callbacks of the
   * HealAPI request. Only a single ref instance is needed because as soon as the component is
   * unmounted, the next render will create a new instance of the component with its own, new
   * AbortController. This could also be used to stifle toasts for non-selected visits, but I
   * was advised not to do this as it would be inconsistent with the toast behavior in other
   * parts of the app.
   */
  const abortController = useRef(new AbortController());
  
  useEffect(() => {
    setIsWaitlisted(visit.isWaitlisted);
    setIsUpdating(false);

    const abortControllerValue = abortController.current;

    // On unmount, trigger the AbortController.
    return () => {
      abortControllerValue.abort();
    }
  }, [visit]);

  const handleChange = useCallback(async (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => {
    const oldValue = isWaitlisted;
    const newValue = checked ?? false;

    /**
     * If the toggle already has a request in flight, ignore the event.
     * This should be made redundant by the disabled prop on the Toggle component,
     * but this is more explicit and thorough.
     */
    if (isUpdating) {
      event.preventDefault();
      return;
    }

    // Update optimistically, then disable the toggle.
    setIsWaitlisted(newValue);
    setIsUpdating(true);

    HealAPI.setWaitlisted(visit.visitCode, checked ?? false)
      .then((response: ApiResponse<any, any>) => {
        if (response.ok) {
          if (onSuccess) {
            /**
             * The third argument passes the asynchronous copy of the visitCode back out to the parent.
             * This ensures that this visit is updated instead of whichever one happens to be selected
             * when the request completes.
             */
            onSuccess(newValue ? 'Added' : 'Removed', `${newValue ? `Added ${visit.visitCode} to` : `Removed ${visit.visitCode} from`} the waitlist.`, visit.visitCode);
          }
        } else {
          // Prevent unmounted rerenders, but allow the toast to display regardless.
          if (!abortController.current.signal.aborted) {
            // If the request fails, revert our optimistic update.
            setIsWaitlisted(oldValue);
          }
          if (onError) {
            onError(`Unable to ${newValue ? `add ${visit.visitCode} to` : `remove ${visit.visitCode} from`} the waitlist. Please try again later.`);
          }
        }
      })
      .catch(() => {
        // Prevent unmounted rerenders, but allow the toast to display regardless.
        if (!abortController.current.signal.aborted) {
          /**
           * If the request throws an error, revert our optimistic update.
           * I don't actually know when APISauce would throw a proper error instead of just
           * setting response.ok to false, but I'm covering both cases, just to be sure.
           */
          setIsWaitlisted(oldValue);
        }
        if (onError) {
          onError(`An error occurred while ${newValue ? `adding ${visit.visitCode} to` : `removing ${visit.visitCode} from`} the waitlist. Please try again later.`);
        }
      })
      .finally(() => {
        // Prevent unmounted rerenders.
        if (!abortController.current.signal.aborted) {
          setIsUpdating(false);
        }
      });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit, isUpdating]);

  return (
    <>
      <div className={styles.sro} id={toggleDescriptionId}>{TOGGLE_DESCRIPTION}</div>
      <Toggle
        aria-describedby={toggleDescriptionId} // Apparently Fluent UI just ignores arbitrary ARIA properties, but
        aria-disabled={isUpdating}             // I'm leaving these in just in case they someday add support for them.
        ariaLabel="Waitlisted"
        onText="Yes"
        offText="No"
        role="checkbox"
        disabled={isUpdating}
        checked={isWaitlisted}
        onChange={handleChange}
      />
    </>
  );
};
